import React, { useState, useEffect } from 'react';
import { Save, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import api from '../../services/api';

const ProjectManagement = () => {
  // Component for managing project information including YouTube URL
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [projectData, setProjectData] = useState({
    title: '',
    description: '',
    location: '',
    budget: '',
    timeline: '',
    status: 'En cours',
    youtube_url: '',
    highlights: []
  });

  useEffect(() => {
    loadProjectData();
  }, []);

  const loadProjectData = async () => {
    setLoading(true);
    try {
      const response = await api.get('/projects');
      if (response.data && response.data.length > 0) {
        setProjectData(response.data[0]);
      }
    } catch (error) {
      console.error('Error loading project:', error);
      toast.error('Erreur lors du chargement du projet');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let response;
      
      if (projectData.id) {
        try {
          // Try to update existing project
          response = await api.put(`/projects/${projectData.id}`, projectData);
        } catch (error) {
          // If 404, create new project instead
          if (error.response?.status === 404) {
            response = await api.post('/projects', projectData);
          } else {
            throw error;
          }
        }
      } else {
        // Create new project
        response = await api.post('/projects', projectData);
      }
      
      if (response?.data) {
        setProjectData(response.data);
      }
      
      toast.success('Projet sauvegardé avec succès');
      loadProjectData(); // Reload to get fresh data
    } catch (error) {
      console.error('Error saving project:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setProjectData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Gestion du Projet</h2>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Sauvegarde...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Sauvegarder
            </>
          )}
        </Button>
      </div>

      <div className="glass rounded-xl p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Titre du Projet
          </label>
          <Input
            value={projectData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            className="bg-slate-700 border-slate-600 text-white"
            placeholder="Nom du projet"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Description
          </label>
          <textarea
            value={projectData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            className="w-full bg-slate-700 border border-slate-600 rounded-md p-3 text-white min-h-[120px]"
            placeholder="Description détaillée du projet"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Localisation
            </label>
            <Input
              value={projectData.location}
              onChange={(e) => handleChange('location', e.target.value)}
              className="bg-slate-700 border-slate-600 text-white"
              placeholder="Ville, Région"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Budget
            </label>
            <Input
              value={projectData.budget}
              onChange={(e) => handleChange('budget', e.target.value)}
              className="bg-slate-700 border-slate-600 text-white"
              placeholder="Ex: 5M €"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Timeline
            </label>
            <Input
              value={projectData.timeline}
              onChange={(e) => handleChange('timeline', e.target.value)}
              className="bg-slate-700 border-slate-600 text-white"
              placeholder="Ex: 2024-2026"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Statut
            </label>
            <select
              value={projectData.status}
              onChange={(e) => handleChange('status', e.target.value)}
              className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-white"
            >
              <option value="En cours">En cours</option>
              <option value="Planifié">Planifié</option>
              <option value="Terminé">Terminé</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            URL du Site (Lien "Visiter le site")
          </label>
          <Input
            value={projectData.url || ''}
            onChange={(e) => handleChange('url', e.target.value)}
            className="bg-slate-700 border-slate-600 text-white"
            placeholder="https://evocore-saas1.vercel.app"
          />
          <p className="text-xs text-gray-400 mt-1">
            URL vers laquelle le bouton "Visiter le site →" redirigera
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            URL Vidéo YouTube (embed)
          </label>
          <Input
            value={projectData.youtube_url}
            onChange={(e) => handleChange('youtube_url', e.target.value)}
            className="bg-slate-700 border-slate-600 text-white"
            placeholder="https://www.youtube.com/embed/VIDEO_ID"
          />
          <p className="text-xs text-gray-400 mt-1">
            Format: https://www.youtube.com/embed/VIDEO_ID
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            URL Image de Présentation
          </label>
          <Input
            value={projectData.presentation_image || ''}
            onChange={(e) => handleChange('presentation_image', e.target.value)}
            className="bg-slate-700 border-slate-600 text-white"
            placeholder="URL de l'image principale"
          />
          <p className="text-xs text-gray-400 mt-1">
            Collez l'URL d'une image ou uploadez-la dans l'onglet "Médias" et copiez son URL
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProjectManagement;
